/**
 * The app shell: sidebar, section content, hash routing.
 *
 * Nine tabs became four sections plus one primary action. The screens
 * themselves did not move - `StartSection`, `WorkSection`, `BillingSection` and
 * `SettingsSection` compose the existing ones, so this file still only decides
 * what is on screen.
 *
 * Routing rules carried over from the live page's nav()/routeToHash():
 *
 *   - The hash IS the route. Navigating writes it; the back button reads it.
 *     The live page set `location.hash` inside nav() and listened for
 *     hashchange, which meant its own navigation fired the listener too. Here
 *     the listener is the single source of truth and navigating just writes the
 *     hash, so there is one path in and no chance of the two disagreeing.
 *   - Every retired tab name is still a valid hash (see routes.ts). A link in
 *     an e-mail from three months ago has to keep working.
 *   - `#...payment=success` is not a section. It is the return from Stripe
 *     Checkout and belongs to onboarding step 5.
 *
 * @module web/screens/AppScreen
 */
import { useCallback, useEffect, useState } from "react";
import { get } from "../api/client.ts";
import type { BriefListResponse } from "../api/types.ts";
import { fmtEur } from "../money.ts";
import { DEFAULT_TAB, parseHash, type BillingView, type Tab, type WorkView } from "../routes.ts";
import { BRIEFS_CHANGED } from "./app/confirmSheet.tsx";
import { pendingCount } from "./app/work/cards.ts";
import { useBrand } from "../state/BrandContext.tsx";
import { Sidebar } from "./app/Sidebar.tsx";
import { MobileTopBar } from "./app/MobileTopBar.tsx";
import { PasswordNudge } from "../components/PasswordNudge.tsx";
import { StartSection } from "./app/StartSection.tsx";
import { WorkSection } from "./app/WorkSection.tsx";
import { BillingSection } from "./app/BillingSection.tsx";
import { SettingsSection } from "./app/SettingsSection.tsx";
import { allowedTab } from "./app/roleView.ts";

/** What GET /api/billing/wallet answers with, for the pill. */
interface WalletSummary {
  balanceCents?: number;
}

/** What the mobile top bar calls each section. */
const TAB_TITLES: Record<Tab, string> = {
  start: "Home",
  work: "Work",
  billing: "Billing",
  settings: "Settings",
};

export function AppScreen() {
  const { brand, signOut, setObStep } = useBrand();
  const [tab, setTab] = useState<Tab>(DEFAULT_TAB);
  const [workView, setWorkView] = useState<WorkView>("list");
  const [billingView, setBillingView] = useState<BillingView>("summary");
  const [wallet, setWallet] = useState<WalletSummary | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  /** `#work/board/<cardId>` — the card the board should open. */
  const [boardCard, setBoardCard] = useState<string | null>(null);
  /** `#start/<submissionId>` — the submission Start should show the price of. */
  const [startSubmission, setStartSubmission] = useState<string | null>(null);
  /** Submissions waiting for a Confirm: the Work badge (spec 2026-09-20). */
  const [pending, setPending] = useState(0);

  /** Read the hash and settle on a section. The one place routing is decided. */
  const applyHash = useCallback(() => {
    const route = parseHash(window.location.hash);
    if (route.kind === "payment-success") {
      // Back from Stripe. Not a section: onboarding resumes at the payment step.
      setObStep(5);
      return;
    }
    if (!allowedTab(brand?.role ?? "owner", route.tab, brand?.billingOwner ?? (brand?.role ?? "owner") === "owner")) {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#start`);
      setTab("start");
      return;
    }
    setTab(route.tab);
    // A deep link may name a view inside the section. Anything unrecognised is
    // ignored rather than blanking the panel - same spirit as the tab fallback.
    if (route.tab === "work" && (route.view === "list" || route.view === "board")) {
      setWorkView(route.view);
    }
    // The ids ride on the hash and are cleared by any hash without them, so a
    // plain `#work/board` after a deep link is a board with nothing pre-opened.
    setBoardCard(route.tab === "work" && route.view === "board" ? route.card ?? null : null);
    setStartSubmission(route.tab === "start" ? route.submission ?? null : null);
    if (route.tab === "billing"
      && (route.view === "summary" || route.view === "wallet" || route.view === "membership")) {
      setBillingView(route.view);
    }
  }, [setObStep, brand?.role, brand?.billingOwner]);

  useEffect(() => {
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
  }, [applyHash]);

  // Stripe hands the browser back with its own parameters and nobody was
  // clearing them, so every screenshot and every shared link carried a payment
  // intent id for the rest of the session.
  useEffect(() => { clearReturnParams(); }, []);

  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setNavOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navOpen]);

  useEffect(() => {
    let live = true;
    // The pill is decoration on a nav item: if it cannot load, the nav still
    // works and the balance simply reads as a dash.
    void get<WalletSummary>("/api/stripe/wallet")
      .then(w => { if (live) setWallet(w); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  // The Work badge: recounted on load, on every navigation, and whenever a
  // screen reports that briefs changed (a request priced, a confirm settled).
  // Decoration, like the pill: a failed read leaves the last count standing.
  useEffect(() => {
    let live = true;
    const recount = () => {
      void get<BriefListResponse>("/api/briefs")
        .then(r => { if (live) setPending(pendingCount(r.briefs ?? [])); })
        .catch(() => {});
    };
    recount();
    window.addEventListener("hashchange", recount);
    window.addEventListener(BRIEFS_CHANGED, recount);
    return () => {
      live = false;
      window.removeEventListener("hashchange", recount);
      window.removeEventListener(BRIEFS_CHANGED, recount);
    };
  }, []);

  function navigate(next: Tab): void {
    // Write the hash and let the listener do the routing - see the module note.
    window.location.hash = next;
    setNavOpen(false);
  }

  function compose(): void {
    // The primary action is a destination, not a mode: it lands on Start, where
    // the compose field is the first thing on the page.
    window.location.hash = "start";
    setNavOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function goWorkView(v: WorkView): void { window.location.hash = `work/${v}`; }
  function goBillingView(v: BillingView): void { window.location.hash = `billing/${v}`; }

  const balance = wallet?.balanceCents ?? 0;
  // The wallet/credit view is shown only when there is credit to show; a
  // permanent grey 0,00 € reads as a deficiency rather than an irrelevance.
  // (Prepaid credit still exists as a top-up under per_card; buffer tracks its
  // own running total separately.)
  const showWallet = balance > 0;

  return (
    <div className="screen-app" data-screen="app-screen">
      <div className="app-layout">
        <MobileTopBar title={TAB_TITLES[tab]} brandName={brand?.name ?? "Brand"} open={navOpen} onToggle={() => setNavOpen(o => !o)} badge={pending} />
        <div
          className={`nav-scrim${navOpen ? " open" : ""}`}
          aria-hidden="true"
          onClick={() => setNavOpen(false)}
        />
        <Sidebar
          tab={tab}
          onNavigate={navigate}
          onCompose={compose}
          onAgentMode={() => { window.location.hash = "settings/agents"; setNavOpen(false); }}
          onSignOut={() => void signOut()}
          walletLabel={wallet ? fmtEur(balance) : "-"}
          walletPositive={balance > 0}
          showWallet={showWallet}
          open={navOpen}
          workBadge={pending}
        />
        <main
          className="main-content"
          data-tab={tab}
          /* The section alone no longer says where you are: `work` is a list or
             a board, `billing` is three things. The harness asserts against
             both, so both have to be on the element. */
          data-view={tab === "work" ? workView : tab === "billing" ? billingView : undefined}
        >
          {/* Above the content, never over it: a magic-link session is asked to
              set up a personal sign-in, and can wave it away for this visit. */}
          <PasswordNudge />
          {tab === "start" ? <StartSection name={brand?.contactName ?? brand?.name ?? null} submissionId={startSubmission} />
            : tab === "work" ? <WorkSection view={workView} onView={goWorkView} card={boardCard} />
            : tab === "billing" ? <BillingSection view={billingView} onView={goBillingView} />
            : <SettingsSection />}
        </main>
      </div>
    </div>
  );
}

/**
 * Take the checkout returns out of the URL.
 *
 * Done as soon as they are read, so a payment intent id does not ride along
 * in every link the customer copies out of the address bar.
 */
function clearReturnParams(): void {
  const url = new URL(window.location.href);
  const junk = [
    "setup_intent_id", "setup_intent", "setup_intent_client_secret",
    "checkout_status", "status", "state_id",
  ];
  let touched = false;
  for (const p of junk) {
    if (url.searchParams.has(p)) { url.searchParams.delete(p); touched = true; }
  }
  if (touched) window.history.replaceState(null, "", url.toString());
}
